import { storeToRefs } from 'pinia'
import { watch, ref } from 'vue'

import { handleRingtoneUri } from '@/entities/call-ringtone'
import { HTMLAudioElementWithSetSinkId, useStoreMediaDevices } from '@/entities/media-devices'
import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { tauriMode } from '@/shared/utils/import-meta'
import { isTypedKeyInObject } from '@/shared/utils/isTypedKeyInObject'

interface ICallSpeaker{
  play: () => Promise<void>
  stop: () => void
  paused: () => boolean
}

export const useIncomingCallSpeaker = (fabricCallSpeaker: ICallSpeaker ) => {
  return fabricCallSpeaker
}

export const createIncomingCallSpeaker = (src?: string) => {
  const audio = document.createElement('audio')

  const isBrowserSupport = isTypedKeyInObject(audio, 'setSinkId', 'function')
  if (isBrowserSupport) {
    return incomingCallSpeakerHTMLElement(src)
  } else {
    return incomingCallSpeakerWebAudioAPI(src)
  }
}

const incomingCallSpeakerHTMLElement : (audioSrc?: string) => ICallSpeaker = (audioSrc?: string) => {
  const { activeIncomingCallSpeakerId, incomingCallSpeakerVolume } = storeToRefs(useStoreMediaDevices())
  const { getPreference } = usePreferencesStore()

  let audio: HTMLAudioElementWithSetSinkId | HTMLAudioElement | undefined

  const releaseAudio = (element: HTMLAudioElementWithSetSinkId | HTMLAudioElement) => {
    element.pause()
    element.currentTime = 0
    element.removeAttribute('src')
    element.load()
  }

  const _init = async () => {
    const src = await handleRingtoneUri(audioSrc ?? getPreference(PreferenceKey.INCOMING_CALL_URI) as string)
    if (tauriMode()) {
      audio = new Audio(src)
      audio.volume = incomingCallSpeakerVolume.value
      audio.loop = true
    } else {
      audio = (new Audio(src)) as unknown as HTMLAudioElementWithSetSinkId
      audio.volume = incomingCallSpeakerVolume.value
      audio.loop = true
      if (isTypedKeyInObject(audio, 'setSinkId', 'function')) {
        (audio as unknown as HTMLAudioElementWithSetSinkId).setSinkId?.(activeIncomingCallSpeakerId.value)
      }
    }
  }

  const play = async () => {
    if (!audio) {
      await _init()
    }
    if (!audio) {
      return
    }
    try {
      await audio.play()
    } catch (e) {
      console.error(e)
    }
  }

  const stop = () => {
    if (!audio) {
      return
    }

    releaseAudio(audio)
    audio = undefined
  }

  watch(activeIncomingCallSpeakerId, () => {
    if (!activeIncomingCallSpeakerId || !audio) {
      return
    }
    if (isTypedKeyInObject(audio, 'setSinkId', 'function')) {
      (audio as unknown as HTMLAudioElementWithSetSinkId).setSinkId?.(activeIncomingCallSpeakerId.value)
    }
  })

  return {
    play,
    stop,
    paused: () => !audio || audio.paused,
  }
}

const incomingCallSpeakerWebAudioAPI : (audioSrc?: string) => ICallSpeaker = (audioSrc?: string) => {
  const audioContext = new window.AudioContext
  const source = audioContext.createBufferSource()
  const gain = audioContext.createGain()
  const audioBuffer = ref<AudioBuffer>()
  const { getPreference } = usePreferencesStore()

  const _init = async () => {
    try {
      const src = await handleRingtoneUri(audioSrc ?? getPreference(PreferenceKey.INCOMING_CALL_URI) as string)
      const response = await fetch(src)
      audioBuffer.value = await audioContext.decodeAudioData(await response.arrayBuffer())
    } catch(error) {
      console.error(error)
    }
  }

  const play = async () => {
    if (!audioBuffer.value) {
      await _init()
    }
    if (!audioBuffer.value) {
      return
    }
    try {
      source.connect(gain)
      source.buffer = audioBuffer.value
      gain.connect(audioContext.destination)
      source.start(0)
    } catch (e) {
      console.error(e)
    }
  }

  const stop = () => {
    if (!audioBuffer.value) {
      return
    }

    source.stop(0)
  }

  return {
    play, stop,
    paused: () => audioContext.state === 'suspended',
  }
}
